// src/context/ThemeContext.tsx

import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import type { Theme, ThemeContextValue } from '../types/theme'

const STORAGE_KEY = 'portfolio-theme'

/*
 * getInitialTheme
 * ──────────────────────────────────────────────
 * Priorité :
 *   1. Valeur persistée en localStorage
 *   2. Préférence système (prefers-color-scheme)
 *   3. Défaut : dark (charte graphique .NET)
 *
 * Appelée HORS du render pour éviter le FOUC
 * (Flash Of Unstyled Content).
 */
function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    // localStorage inaccessible (mode privé strict, etc.)
  }

  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light'

  return 'dark'
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  toggleTheme: () => {},
  isDark: true,
})

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  /*
   * ⚠️ La classe DOIT être posée sur <html> (document.documentElement),
   *    pas sur <body>, pour que les variables CSS du :root soient
   *    disponibles avant tout paint.
   */
  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)

    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // silencieux
    }
  }, [theme])

  /* Synchronise avec la préférence système si pas de valeur stockée */
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) {
          setTheme(e.matches ? 'dark' : 'light')
        }
      } catch {
        setTheme(e.matches ? 'dark' : 'light')
      }
    }

    mq.addEventListener('change', handleChange)
    return () => mq.removeEventListener('change', handleChange)
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])

  return (
    <ThemeContext.Provider
      value={{ theme, toggleTheme, isDark: theme === 'dark' }}
    >
      {children}
    </ThemeContext.Provider>
  )
}